import {
  DollarSign,
  Flag,
  ShieldOff,
  Search,
  MapPin,
  PenTool,
  Star,
  Smartphone,
  Bot,
  ClipboardCheck,
  TrendingUp,
  Megaphone,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export type ArticleCategory =
  | "Pricing & Budget"
  | "Web Design"
  | "SEO"
  | "Google Maps"
  | "AI & GEO"
  | "Buyer's Guide"
  | "Reviews & Reputation"
  | "Marketing Strategy";

export type IndustryTag =
  | "Home Services"
  | "Professional Services"
  | "Health & Wellness"
  | "Retail, Food & Auto"
  | "All Industries";

export type Article = {
  slug: string; // full path
  title: string;
  desc: string;
  category: ArticleCategory;
  industries: IndustryTag[];
  read: string;
  icon: LucideIcon;
  /** Imported hero image URL (ES6 import → bundled URL string) */
  image?: string;
  /** true if the article file already exists from the legacy Honest Answers cluster */
  legacy?: boolean;
  /** ISO date (YYYY-MM-DD) — original publish date, used for sitemap + JSON-LD */
  datePublished?: string;
  /** ISO date (YYYY-MM-DD) — last meaningful content edit, used as sitemap lastmod */
  dateUpdated?: string;
  /** Optional free-form tags used for related-article scoring (besides category + industries). */
  tags?: string[];
};

/**
 * Curated "next read" map — the editorial sequel for each article. When set,
 * this slug is always surfaced first in the related-articles module so readers
 * have a clear, intentional next step rather than only algorithm-driven picks.
 *
 * Keys and values are full article slugs from ARTICLES below.
 */
export const NEXT_READ_MAP: Record<string, string> = {
  "/learn/local-seo-guide": "/learn/google-business-profile",
  "/learn/google-business-profile": "/learn/get-more-reviews",
  "/learn/get-more-reviews": "/learn/website-conversion",
  "/learn/website-conversion": "/learn/mobile-website-mistakes",
  "/learn/mobile-website-mistakes": "/learn/service-area-pages",
  "/learn/service-area-pages": "/learn/local-seo-guide",
  "/learn/seo-vs-ppc": "/learn/marketing-budget",
  "/learn/marketing-budget": "/honest-answers/website-cost",
  "/learn/ai-search-geo": "/learn/great-decoupling-colorado",
  "/learn/great-decoupling-colorado": "/learn/aeo-geo-aio-explained",
  "/learn/aeo-geo-aio-explained": "/learn/ai-vs-human-marketing",
  "/learn/ai-vs-human-marketing": "/learn/agency-questions",
  "/learn/agency-questions": "/honest-answers/red-flags",
  "/honest-answers/website-cost": "/learn/marketing-budget",
  "/honest-answers/red-flags": "/learn/agency-questions",
  "/honest-answers/not-a-fit": "/learn/agency-questions",
  "/learn/local-seo-vs-freelancer": "/learn/local-vs-national-seo-agency",
  "/learn/local-vs-national-seo-agency": "/learn/local-seo-vs-lead-marketplaces",
  "/learn/local-seo-vs-lead-marketplaces": "/learn/marketing-budget",
  "/learn/web-design-vs-freelancer": "/learn/custom-web-design-vs-diy-platforms",
  "/learn/custom-web-design-vs-diy-platforms": "/honest-answers/website-cost",
  "/learn/agency-vs-in-house-marketer": "/learn/agency-questions",
};

// ---- Hero images (one per article) ----
import imgLocalSeo from "@/assets/learn/local-seo-guide.jpg";
import imgGbp from "@/assets/learn/google-business-profile.jpg";
import imgConversion from "@/assets/learn/website-conversion.jpg";
import imgReviews from "@/assets/learn/get-more-reviews.jpg";
import imgMobile from "@/assets/learn/mobile-website-mistakes.jpg";
import imgAiGeo from "@/assets/learn/ai-search-geo.jpg";
import imgSeoVsPpc from "@/assets/learn/seo-vs-ppc.jpg";
import imgAgencyQs from "@/assets/learn/agency-questions.jpg";
import imgServiceArea from "@/assets/learn/service-area-pages.jpg";
import imgBudget from "@/assets/learn/marketing-budget.jpg";
import imgWebsiteCost from "@/assets/learn/website-cost.jpg";
import imgRedFlags from "@/assets/learn/red-flags.jpg";
import imgNotAFit from "@/assets/learn/not-a-fit.jpg";
import imgGreatDecoupling from "@/assets/learn/great-decoupling.jpg";
import imgAiVsHuman from "@/assets/learn/ai-vs-human-marketing.jpg";
import imgAcronyms from "@/assets/learn/aeo-geo-aio-explained.jpg";

/** Fallback dates when an article hasn't been individually dated yet.
 *  Match the defaults baked into ArticleLayout so JSON-LD stays consistent. */
export const ARTICLE_DEFAULT_PUBLISHED = "2025-01-15";
export const ARTICLE_DEFAULT_UPDATED = "2025-04-26";

/** Resolve the lastmod date for an article (dateUpdated → datePublished → default). */
export function articleLastmod(a: Article): string {
  return a.dateUpdated ?? a.datePublished ?? ARTICLE_DEFAULT_UPDATED;
}

export const CATEGORIES: ArticleCategory[] = [
  "Pricing & Budget",
  "Web Design",
  "SEO",
  "Google Maps",
  "AI & GEO",
  "Buyer's Guide",
  "Reviews & Reputation",
  "Marketing Strategy",
];

export const INDUSTRIES: IndustryTag[] = [
  "All Industries",
  "Home Services",
  "Professional Services",
  "Health & Wellness",
  "Retail, Food & Auto",
];

// 10 NEW articles + 3 existing legacy Honest Answers articles
export const ARTICLES: Article[] = [
  {
    slug: "/learn/web-design-in-colorado",
    title: "Web design in Colorado: the complete buyer's guide",
    desc: "Real prices, real timelines, and the questions to ask before hiring a Colorado web design company.",
    category: "Web Design",
    industries: ["All Industries"],
    read: "12 min read",
    icon: Wrench,
    image: imgWebsiteCost,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/colorado-seo-services",
    title: "Colorado SEO services: what they cost and what actually works",
    desc: "What real Colorado SEO includes, what it should cost per month, and how to spot a bad contract.",
    category: "SEO",
    industries: ["All Industries"],
    read: "12 min read",
    icon: Wrench,
    image: imgLocalSeo,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/seo-companies-near-me-colorado",
    title: "SEO companies near me: how to choose one in Colorado",
    desc: "How to tell a real local SEO company from a call center — what to ask, what it costs, and the red flags.",
    category: "SEO",
    industries: ["All Industries"],
    read: "10 min read",
    icon: Search,
    image: imgLocalSeo,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/best-seo-companies-colorado",
    title: "What makes the best SEO company: a Colorado buyer's checklist",
    desc: "Five verifiable things that separate real SEO companies from good salespeople.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "10 min read",
    icon: Star,
    image: imgAgencyQs,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/small-business-seo-colorado",
    title: "Small business SEO in Colorado: where to start",
    desc: "What to fix first, what it costs, and how a single-location business beats bigger competitors locally.",
    category: "SEO",
    industries: ["All Industries"],
    read: "11 min read",
    icon: MapPin,
    image: imgBudget,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/website-developers-near-me-colorado",
    title: "Website developers near me: when local matters and when it doesn't",
    desc: "Local vs remote developers, honest Colorado prices, and the questions to ask before hiring.",
    category: "Web Design",
    industries: ["All Industries"],
    read: "9 min read",
    icon: PenTool,
    image: imgWebsiteCost,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/social-media-marketing-small-business-colorado",
    title: "Social media marketing for small business in Colorado",
    desc: "Which platforms matter, what to post, realistic results, and what management should cost.",
    category: "Marketing Strategy",
    industries: ["All Industries"],
    read: "10 min read",
    icon: Megaphone,
    image: imgMobile,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/wordpress-website-designer-colorado",
    title: "WordPress website designer in Colorado: when WordPress makes sense",
    desc: "When WordPress is the right call, when it isn't, and what a WordPress site should cost in Colorado.",
    category: "Web Design",
    industries: ["All Industries"],
    read: "9 min read",
    icon: PenTool,
    image: imgWebsiteCost,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/seo-packages-colorado",
    title: "SEO packages and monthly SEO services: what should actually be included",
    desc: "What monthly SEO services should include, honest Colorado pricing, and the red flags to avoid.",
    category: "SEO",
    industries: ["All Industries"],
    read: "9 min read",
    icon: Search,
    image: imgSeoVsPpc,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/seo-for-contractors-colorado",
    title: "SEO for contractors: how Colorado trades win page one",
    desc: "How Colorado roofers, plumbers, HVAC and electricians rank in the Map Pack and organic search.",
    category: "SEO",
    industries: ["Home Services"],
    read: "10 min read",
    icon: Wrench,
    image: imgLocalSeo,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/website-redesign-services-colorado",
    title: "Website redesign services: when to redesign, when to rebuild, and what it costs",
    desc: "When a refresh is enough, when you need a rebuild, and how to redesign without losing rankings.",
    category: "Web Design",
    industries: ["All Industries"],
    read: "9 min read",
    icon: PenTool,
    image: imgWebsiteCost,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/seo-consultant-colorado-springs",
    title: "Hiring an SEO consultant in Colorado Springs: a buyer's guide",
    desc: "What an SEO consultant should actually do, what it costs, and the questions that expose pretenders.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "8 min read",
    icon: ClipboardCheck,
    image: imgAgencyQs,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/cheap-seo-true-cost-colorado",
    title: "Cheap SEO: what it really costs your Colorado business",
    desc: "What $99–$500/month actually buys, the damage it can do, and what real SEO costs in Colorado.",
    category: "SEO",
    industries: ["All Industries"],
    read: "9 min read",
    icon: Search,
    image: imgBudget,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/companies-that-build-websites-colorado",
    title: "Companies that build websites: how to choose one in Colorado",
    desc: "The four types of website companies, what each really delivers, and honest Colorado prices.",
    category: "Web Design",
    industries: ["All Industries"],
    read: "10 min read",
    icon: Wrench,
    image: imgAgencyQs,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/website-and-seo-services-colorado",
    title: "Website and SEO services: why they work better together",
    desc: "Why the build and the rankings belong in one plan — and what bundled services cost in Colorado.",
    category: "SEO",
    industries: ["All Industries"],
    read: "9 min read",
    icon: Search,
    image: imgLocalSeo,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/website-designers-for-small-business-colorado",
    title: "Website designers for small business in Colorado: what to look for",
    desc: "What a small business site actually needs, honest prices, and the red flags that cost owners thousands.",
    category: "Web Design",
    industries: ["All Industries"],
    read: "10 min read",
    icon: PenTool,
    image: imgMobile,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/web-design-firms-colorado",
    title: "Web design firms in Colorado: how to choose the right one",
    desc: "The three types of web design firms, honest Colorado pricing, and the questions that expose resellers.",
    category: "Web Design",
    industries: ["All Industries"],
    read: "9 min read",
    icon: PenTool,
    image: imgAgencyQs,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/social-media-management-agency-colorado",
    title: "Social media management agency: what you're actually paying for",
    desc: "What real social media management includes, what it costs in Colorado, and why posting alone doesn't grow a business.",
    category: "Marketing Strategy",
    industries: ["All Industries"],
    read: "8 min read",
    icon: Megaphone,
    image: imgBudget,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/ecommerce-web-design-colorado",
    title: "Ecommerce web design in Colorado: costs, platforms, and what actually matters",
    desc: "Honest ecommerce costs in Colorado, platform choices in plain English, and what makes an online store sell.",
    category: "Web Design",
    industries: ["Retail, Food & Auto"],
    read: "9 min read",
    icon: PenTool,
    image: imgWebsiteCost,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/seo-agency-reviews-colorado",
    title: "How to read SEO agency reviews (and spot the fake ones)",
    desc: "The patterns that give fake reviews away, and how to verify an agency before you sign anything.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "8 min read",
    icon: Star,
    image: imgReviews,
    datePublished: "2026-10-17",
    dateUpdated: "2026-10-17",
  },
  {
    slug: "/learn/roofing-leads-colorado",
    title: "How Colorado roofers get more leads (without buying shared leads)",
    desc: "Google Maps, storm pages, reviews, and ads that bring Colorado roofers their own calls.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgLocalSeo,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/hvac-leads-colorado",
    title: "How Colorado HVAC companies get more calls year-round",
    desc: "Google Maps, seasonal pages, maintenance plans, and weather-timed ads for Colorado HVAC.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgGbp,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/plumbing-leads-colorado",
    title: "How Colorado plumbers get more emergency and repair calls",
    desc: "Emergency searches, frozen-pipe season, and reviews that win the call for Colorado plumbers.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgConversion,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/electrician-leads-colorado",
    title: "How Colorado electricians get more service and upgrade calls",
    desc: "Panel, EV charger, and generator pages plus reviews for Colorado electricians.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgLocalSeo,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/organic-leads-colorado-contractors",
    title: "Free leads first: how Colorado contractors earn calls before paying for ads",
    desc: "Website, SEO, Google Maps, AI search, and social media before paid platforms.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgLocalSeo,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/concrete-leads-colorado",
    title: "How Colorado concrete contractors get more calls",
    desc: "Project pages, photos, and reviews for patio and driveway jobs.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgGbp,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/fence-deck-leads-colorado",
    title: "How Colorado fence and deck builders get more quotes",
    desc: "Material pages, HOA help, and Google Maps photos.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgConversion,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/tree-care-leads-colorado",
    title: "How Colorado tree care companies get more calls",
    desc: "Fire mitigation and storm cleanup pages plus reviews.",
    category: "Marketing Strategy",
    industries: ["Home Services"],
    read: "6 min read",
    icon: Wrench,
    image: imgLocalSeo,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  // === NEW (10) ===
  {
    slug: "/learn/local-seo-guide",
    title: "The plain-English guide to local SEO for Colorado small businesses",
    desc: "Exactly what local SEO is, how it works in 2026, and the 6 things that actually move the needle in Colorado Springs, Denver, and the Front Range.",
    category: "SEO",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "11 min read",
    icon: Search,
    image: imgLocalSeo,
    datePublished: "2025-01-08",
    dateUpdated: "2025-04-26",
  },
  {
    slug: "/learn/google-business-profile",
    title: "How to rank in the Google Maps 3-pack (without paying for ads)",
    desc: "A step-by-step Google Business Profile playbook used by real Colorado HVAC, plumbing, and roofing companies to dominate local map results.",
    category: "Google Maps",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "10 min read",
    icon: MapPin,
    image: imgGbp,
    datePublished: "2025-01-15",
    dateUpdated: "2025-04-22",
  },
  {
    slug: "/learn/website-conversion",
    title: "Why your website gets traffic but no calls (and how to fix it)",
    desc: "10 conversion killers we find on almost every Colorado small-business website — plus exactly what to change today to start turning visitors into booked jobs.",
    category: "Web Design",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "9 min read",
    icon: PenTool,
    image: imgConversion,
    datePublished: "2025-01-22",
    dateUpdated: "2025-04-18",
  },
  {
    slug: "/learn/get-more-reviews",
    title: "How to get 5-star Google reviews from Colorado customers (the right way)",
    desc: "The exact ask, the timing, the templates, and why review-gating will get your account suspended. Built for service businesses with 1–50 employees.",
    category: "Reviews & Reputation",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "7 min read",
    icon: Star,
    image: imgReviews,
    datePublished: "2025-02-03",
    dateUpdated: "2025-04-10",
  },
  {
    slug: "/learn/mobile-website-mistakes",
    title: "7 mobile website mistakes costing Colorado service businesses calls",
    desc: "70% of your traffic is on a phone. Here's where most contractor and service sites fail — and the fixes that actually move the booking rate.",
    category: "Web Design",
    industries: ["Home Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "8 min read",
    icon: Smartphone,
    image: imgMobile,
    datePublished: "2025-02-12",
    dateUpdated: "2025-04-15",
  },
  {
    slug: "/learn/ai-search-geo",
    title: "AI search is here. What ChatGPT, Perplexity, and Google AI mean for your local business",
    desc: "Generative Engine Optimization (GEO) explained without hype — what's changing, what isn't, and what Colorado businesses should actually do in 2026.",
    category: "AI & GEO",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "10 min read",
    icon: Bot,
    image: imgAiGeo,
    datePublished: "2025-02-24",
    dateUpdated: "2025-04-26",
  },
  {
    slug: "/learn/seo-vs-ppc",
    title: "SEO vs Google Ads: which one should a Colorado small business invest in first?",
    desc: "An honest budget-by-budget breakdown. When PPC is the smart play, when SEO wins long-term, and when you actually need both.",
    category: "Marketing Strategy",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "9 min read",
    icon: TrendingUp,
    image: imgSeoVsPpc,
    datePublished: "2025-03-05",
    dateUpdated: "2025-04-12",
  },
  {
    slug: "/learn/agency-questions",
    title: "12 questions to ask before hiring any Colorado SEO or web design agency",
    desc: "Steal our buyer's-guide checklist. The answers will tell you in 5 minutes whether the agency does the work in-house — or quietly outsources it.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "8 min read",
    icon: ClipboardCheck,
    image: imgAgencyQs,
    datePublished: "2025-03-14",
    dateUpdated: "2025-04-20",
  },
  {
    slug: "/learn/service-area-pages",
    title: "Service area & city landing pages: how to do them right (and not get penalized)",
    desc: "The right way to build pages for every Colorado town you serve — without the doorway-page tactics that get sites tanked by Google.",
    category: "SEO",
    industries: ["Home Services"],
    read: "8 min read",
    icon: MapPin,
    image: imgServiceArea,
    datePublished: "2025-03-26",
    dateUpdated: "2025-04-24",
  },
  {
    slug: "/learn/marketing-budget",
    title: "How much should a Colorado small business spend on marketing in 2026?",
    desc: "Real percentage-of-revenue benchmarks by industry, plus a simple formula to figure out your own number — no fluff, no upsell.",
    category: "Pricing & Budget",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "8 min read",
    icon: Megaphone,
    image: imgBudget,
    datePublished: "2025-04-08",
    dateUpdated: "2025-04-26",
  },
  {
    slug: "/learn/start-a-business-in-colorado",
    title: "How to start a business in Colorado (and actually get found online)",
    desc: "Step-by-step: register an LLC, get your Colorado business license, file with the Secretary of State — plus the online setup that decides whether the phone ever rings.",
    category: "Marketing Strategy",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "12 min read",
    icon: Flag,
    image: imgBudget,
    datePublished: "2026-05-28",
    dateUpdated: "2026-05-28",
    tags: ["new business", "llc", "licensing", "secretary of state", "launch"],
  },
  {
    slug: "/learn/first-time-website-owner-guide",
    title: "First-time website owner's guide: what to know before you buy",
    desc: "Plain-English guide for owners building their first website — what hosting, domain, content, and SEO actually mean, and what they cost.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "11 min read",
    icon: ClipboardCheck,
    image: imgBudget,
    datePublished: "2026-05-29",
    dateUpdated: "2026-05-29",
    tags: ["first website", "new owner", "buyer guide", "hosting", "domain"],
  },
  {
    slug: "/learn/new-business-website-checklist",
    title: "New business website checklist: launch in 30 days",
    desc: "Exact 30-day checklist for a new Colorado business: domain, hosting, content, Google Business Profile, schema, and the first 10 backlinks.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "10 min read",
    icon: ClipboardCheck,
    image: imgBudget,
    datePublished: "2026-05-29",
    dateUpdated: "2026-05-29",
    tags: ["checklist", "launch", "new business", "website"],
  },
  {
    slug: "/learn/franchise-location-website-guide",
    title: "Franchise location website guide: corporate site + local pages that rank",
    desc: "How franchise owners should handle a Colorado location website when corporate already has one — locator pages, GBP, schema, and SEO conflicts.",
    category: "Marketing Strategy",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "12 min read",
    icon: Flag,
    image: imgBudget,
    datePublished: "2026-05-29",
    dateUpdated: "2026-05-29",
    tags: ["franchise", "multi-location", "local seo", "gbp"],
  },
  {
    slug: "/learn/buying-an-existing-business-website",
    title: "Buying an existing business? Audit the website before you close",
    desc: "What to inspect on a website, GBP, and backlink profile before you buy a Colorado business — and how to avoid inheriting an SEO penalty.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "9 min read",
    icon: ClipboardCheck,
    image: imgBudget,
    datePublished: "2026-05-29",
    dateUpdated: "2026-05-29",
    tags: ["acquisition", "due diligence", "audit", "buying a business"],
  },

  // === LEGACY (3 existing Honest Answers articles, kept and surfaced here) ===
  {
    slug: "/honest-answers/website-cost",
    title: "How much does a website really cost for a Colorado home-service business?",
    desc: "Real ranges, what drives the price up or down, and what you should actually expect to pay in 2026.",
    category: "Pricing & Budget",
    industries: ["Home Services"],
    read: "9 min read",
    icon: DollarSign,
    image: imgWebsiteCost,
    legacy: true,
    datePublished: "2024-11-12",
    dateUpdated: "2025-04-14",
  },
  {
    slug: "/honest-answers/red-flags",
    title: "10 red flags when hiring a Colorado SEO or web design company",
    desc: "If your agency does any of these — run. Built from 20+ years of cleaning up other people's messes.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "8 min read",
    icon: Flag,
    image: imgRedFlags,
    legacy: true,
    datePublished: "2024-12-03",
    dateUpdated: "2025-04-08",
  },
  {
    slug: "/honest-answers/not-a-fit",
    title: "When Colorado Web Impressions is NOT the best fit for your business",
    desc: "We turn down work every month. Here's exactly when we'll tell you to hire someone else.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "6 min read",
    icon: ShieldOff,
    image: imgNotAFit,
    legacy: true,
    datePublished: "2024-12-18",
    dateUpdated: "2025-04-02",
  },
  {
    slug: "/learn/sitemap-zero-urls-error",
    title: "\"0 URLs, 1 error\" in Search Console: when it's a real problem and when it isn't",
    desc: "Two common Google Search Console sitemap errors — empty news sitemap and empty published-articles sitemap — explained, with the exact rule for when to ignore vs fix them.",
    category: "SEO",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "5 min read",
    icon: Search,
    datePublished: "2026-05-02",
    dateUpdated: "2026-05-02",
  },
  {
    slug: "/learn/great-decoupling-colorado",
    title: "Why your Google traffic is dropping even though you're ranking higher",
    desc: "AI Overviews are quietly cutting clicks to top-ranked Colorado business sites by 30%+. Here's what 'The Great Decoupling' actually means — and what to look at instead of rank reports.",
    category: "AI & GEO",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "8 min read",
    icon: Bot,
    image: imgGreatDecoupling,
    datePublished: "2026-05-02",
    dateUpdated: "2026-05-02",
    tags: ["ai overviews", "geo", "search console", "great decoupling"],
  },
  {
    slug: "/learn/ai-vs-human-marketing",
    title: "Will AI replace your marketing agency? Honest answer from 25 years in",
    desc: "AI won't replace good marketers — it will replace ones who don't adapt. The candid take from a Colorado web pro on the hybrid model that's actually working in 2026.",
    category: "Marketing Strategy",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "9 min read",
    icon: TrendingUp,
    image: imgAiVsHuman,
    datePublished: "2026-05-02",
    dateUpdated: "2026-05-02",
    tags: ["ai", "marketing strategy", "consulting"],
  },
  {
    slug: "/learn/aeo-geo-aio-explained",
    title: "AEO, GEO, AIO, SGE — what all these new SEO acronyms actually mean (and which matter)",
    desc: "Plain-English glossary of AEO, GEO, AIO, and SGE — ranked by what actually matters for a Colorado small business in 2026 (and which ones you can safely ignore).",
    category: "AI & GEO",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    read: "7 min read",
    icon: Bot,
    image: imgAcronyms,
    datePublished: "2026-05-02",
    dateUpdated: "2026-05-02",
    tags: ["aeo", "geo", "aio", "sge", "ai overviews"],
  },

  // === Buyer's Guide cluster — vs/comparison posts (converted from category landing pages) ===
  {
    slug: "/learn/local-seo-vs-freelancer",
    title: "Local SEO agency vs. solo freelancer: honest tradeoffs for a Colorado small business",
    desc: "Local SEO agency vs. freelancer — bench depth, vacation risk, response time, and true monthly cost compared, plus the cases where a freelancer is the smarter hire.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "8 min read",
    icon: ClipboardCheck,
    image: imgAgencyQs,
    datePublished: "2026-05-20",
    dateUpdated: "2026-05-20",
    tags: ["agency vs freelancer", "buyer's guide", "seo"],
  },
  {
    slug: "/learn/local-seo-vs-lead-marketplaces",
    title: "Owned local SEO vs. pay-per-lead marketplaces: the real cost-per-booked-job math",
    desc: "Local SEO vs. pay-per-lead marketplaces — why shared marketplace leads end up costing 3–5x more per booked job than local SEO + Maps, and how to phase between them.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "9 min read",
    icon: DollarSign,
    image: imgBudget,
    datePublished: "2026-05-20",
    dateUpdated: "2026-05-20",
    tags: ["pay per lead", "buyer's guide", "seo", "google maps"],
  },
  {
    slug: "/learn/web-design-vs-freelancer",
    title: "Web design agency vs. solo freelancer: what actually changes for a Colorado business",
    desc: "Local web design agency vs. solo freelancer — ownership, SEO foundation, post-launch support, and timeline differences, plus when a freelancer is the right call.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "7 min read",
    icon: PenTool,
    image: imgConversion,
    datePublished: "2026-05-20",
    dateUpdated: "2026-05-20",
    tags: ["web design", "buyer's guide", "agency vs freelancer"],
  },
  {
    slug: "/learn/local-vs-national-seo-agency",
    title: "Local SEO agency vs. national SEO chain: an owner's honest comparison",
    desc: "Local SEO agency vs. national SEO chain — owner access, contract length, pricing transparency, and Map Pack focus compared by a Colorado Springs SEO owner.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "9 min read",
    icon: Search,
    image: imgRedFlags,
    datePublished: "2026-05-20",
    dateUpdated: "2026-05-20",
    tags: ["local vs national", "buyer's guide", "seo"],
  },
  {
    slug: "/learn/custom-web-design-vs-diy-platforms",
    title: "Custom web design vs. national DIY platforms: ownership, SEO, and the long-term math",
    desc: "Custom web design vs. national DIY platforms — what you trade off on ownership, SEO foundation, and 5-year cost when you pick a subscription builder over a custom site.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "8 min read",
    icon: Smartphone,
    image: imgMobile,
    datePublished: "2026-05-20",
    dateUpdated: "2026-05-20",
    tags: ["web design", "buyer's guide", "diy platforms"],
  },
  {
    slug: "/learn/agency-vs-in-house-marketer",
    title: "Hire a local marketing agency or your first in-house marketer? Real cost and capability math",
    desc: "Marketing agency vs. in-house marketer — a multi-discipline marketing agency at $1,500–$3,000/mo vs. a $90K–$120K loaded in-house hire, with the real Colorado math.",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "9 min read",
    icon: Megaphone,
    image: imgBudget,
    datePublished: "2026-05-20",
    dateUpdated: "2026-05-20",
    tags: ["agency vs in-house", "buyer's guide", "marketing strategy"],
  },
  {
    slug: "/learn/escape-marketing-agency-contracts-colorado",
    title: "Colorado business owner's guide to marketing agency contracts: month-to-month vs. locked-in",
    desc: "How to spot 90-day, 6-month, and annual marketing lock-ins, what to ask before you sign, and why month-to-mont",
    category: "Buyer's Guide",
    industries: ["All Industries"],
    read: "6 min read",
    icon: Megaphone,
    image: imgAgencyQs,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/page-10-to-page-1-seo-colorado",
    title: "From page 10 to page 1: how Colorado businesses climb Google",
    desc: "The step-by-step process Chris Heidlebaugh has used for 27 years to move Colorado business websites from pages",
    category: "SEO",
    industries: ["All Industries"],
    read: "6 min read",
    icon: TrendingUp,
    image: imgLocalSeo,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/social-media-views-colorado-business",
    title: "How Colorado businesses reach 10,000 to 100,000+ social media views a month",
    desc: "Short video, local hooks, and consistent posting that have helped Colorado businesses grow from a few hundred ",
    category: "Marketing Strategy",
    industries: ["All Industries"],
    read: "6 min read",
    icon: Megaphone,
    image: imgReviews,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/fix-google-ads-wasted-spend-colorado",
    title: "How to fix wasted Google Ads spend for Colorado businesses",
    desc: "Common Google Ads problems that waste money \u2014 wrong locations, broad keywords, missing negatives, poor trackin",
    category: "Pricing & Budget",
    industries: ["All Industries"],
    read: "6 min read",
    icon: Search,
    image: imgSeoVsPpc,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
  {
    slug: "/learn/rank-in-ai-search-colorado",
    title: "How Colorado businesses get recommended by ChatGPT, Google AI, and other AI search tools",
    desc: "How to show up when customers ask AI tools for local recommendations: clear service pages, honest pricing, con",
    category: "AI & GEO",
    industries: ["All Industries"],
    read: "6 min read",
    icon: Search,
    image: imgAiGeo,
    datePublished: "2026-10-16",
    dateUpdated: "2026-10-16",
  },
];

/** Lookup an article by its full path (slug). Returns undefined if not found. */
export function getArticleByPath(path: string): Article | undefined {
  return ARTICLES.find((a) => a.slug === path);
}

// =====================================================================
// Video library — real videos from the Colorado Web Impressions YouTube channel
// Each video gets its own page at /learn/videos/$videoSlug with the embed.
// =====================================================================

export type LearnVideo = {
  slug: string; // URL slug (no leading slash)
  youtubeId: string;
  title: string;
  desc: string;
  category: ArticleCategory;
  industries: IndustryTag[];
  length: string; // display string e.g. "3 min watch"
  icon: LucideIcon;
  datePublished?: string;
  dateUpdated?: string;
};

export const VIDEOS: LearnVideo[] = [
  {
    slug: "do-you-own-your-website",
    youtubeId: "bNkptRz4LWg",
    title: "Do you own your website? The #1 question to ask before a new build",
    desc: "Chris explains why so many small businesses don't actually own the website they paid for — and the simple question that protects you before you ever sign a contract.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    length: "Watch",
    icon: ClipboardCheck,
    datePublished: "2025-01-10",
  },
  {
    slug: "are-we-a-good-fit",
    youtubeId: "RqdSho8DCDM",
    title: "Are we a good fit? An honest look at who we work best with",
    desc: "We turn down work every month. Here's the kind of Colorado business we partner with best — and who we'll happily refer somewhere else.",
    category: "Buyer's Guide",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    length: "Watch",
    icon: ShieldOff,
    datePublished: "2025-01-15",
  },
  {
    slug: "what-platform-for-your-website",
    youtubeId: "Rf2CUNtYfYY",
    title: "What platform should you choose for your new website?",
    desc: "WordPress, Wix, Squarespace, Shopify, hand-coded — what's actually the right pick for a Colorado small business in 2026? Chris breaks down the trade-offs.",
    category: "Web Design",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    length: "Watch",
    icon: PenTool,
    datePublished: "2025-02-01",
  },
  {
    slug: "how-to-choose-a-domain-name",
    youtubeId: "f_wKKAShNkQ",
    title: "How to choose a domain name (the truth you need to know)",
    desc: "Short vs. keyword domains, .com vs. .net, hyphens, location words — the real-world rules for picking a domain you won't regret in 5 years.",
    category: "Web Design",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    length: "Watch",
    icon: PenTool,
    datePublished: "2025-02-12",
  },
  {
    slug: "how-long-for-seo-results",
    youtubeId: "pjE11dgh4cA",
    title: "How long does it take to see results from SEO?",
    desc: "An honest timeline for Colorado small businesses — what you should reasonably expect at 30, 60, 90 days, and 6+ months, and why anyone promising faster is lying.",
    category: "SEO",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    length: "Watch",
    icon: Search,
    datePublished: "2025-03-01",
  },
  {
    slug: "how-long-to-build-a-website",
    youtubeId: "9oWME4pYyhU",
    title: "How long does it take to build a website?",
    desc: "Realistic timelines for a small-business website done right — from kickoff to launch — and what slows projects down (hint: it's usually not the agency).",
    category: "Web Design",
    industries: ["Home Services", "Professional Services", "Health & Wellness", "Retail, Food & Auto"],
    length: "Watch",
    icon: PenTool,
    datePublished: "2025-03-15",
  },
];

export function getVideoBySlug(slug: string): LearnVideo | undefined {
  return VIDEOS.find((v) => v.slug === slug);
}

// Re-export icon for hub usage
export { Wrench };

// =====================================================================
// Topic landing page metadata (one per ArticleCategory).
// Used to generate dedicated SEO landing pages at /learn/topic/$slug
// with unique titles, meta descriptions, intros, and on-page copy
// targeted at Colorado small-business owners.
// =====================================================================

export type TopicLandingFaq = { q: string; a: string };

export type TopicLanding = {
  category: ArticleCategory;
  slug: string; // URL slug used at /learn/topic/$slug
  metaTitle: string; // <60 chars
  metaDescription: string; // <160 chars
  h1: string;
  eyebrow: string;
  intro: string;
  // 2-3 short paragraphs of unique on-page copy (Colorado-specific)
  body: string[];
  // Plain-text bullets that round out E-E-A-T signal on the page
  highlights: string[];
  faqs: TopicLandingFaq[];
};

export const TOPIC_LANDINGS: TopicLanding[] = [
  {
    category: "SEO",
    slug: "seo",
    metaTitle: "Colorado SEO Guides for Small Businesses | Learn (Blog)",
    metaDescription:
      "Plain-English SEO guides for Colorado Springs, Denver & Front Range small businesses. Local SEO, service-area pages, and ranking tactics that work in 2026.",
    h1: "Colorado SEO guides for small businesses",
    eyebrow: "Colorado SEO Guide",
    intro:
      "Honest, jargon-free SEO articles for Colorado service businesses — written by a 25-year, in-house, Colorado Springs–based agency that does the work.",
    body: [
      "SEO in Colorado is a different game than SEO in Dallas or Atlanta. Front Range search results are dominated by hyper-local intent: people in Colorado Springs searching at 9pm for an emergency plumber, homeowners in Castle Rock comparing two roofers, and Denver office managers looking for a commercial HVAC company within 15 miles.",
      "These guides explain exactly how Google ranks Colorado businesses today — what actually moves the needle (Google Business Profile, real local content, fast mobile pages, real reviews) and what wastes your budget (link farms, AI-spun content, generic 50-state agencies).",
      "Every article is written by Chris, the owner. No outsourcing, no AI bots, no whitelabel. If you have a follow-up question, the same person who wrote the article will answer the phone.",
    ],
    highlights: [
      "Built for Colorado Springs, Denver, Pueblo, and the Front Range",
      "Real ranking tactics — not generic 'do SEO' advice",
      "Owner-written, in-house, no outsourcing",
    ],
    faqs: [
      {
        q: "How long does SEO take to work for a Colorado small business?",
        a: "For a brand-new Colorado service site, expect meaningful local Maps movement in 60–120 days and stronger organic search rankings in 4–8 months. Established sites with real local presence often see lift in 30–60 days.",
      },
      {
        q: "Do I need separate SEO pages for every Colorado town I serve?",
        a: "Yes — but only if each page has genuinely unique, useful content for that town. Doorway pages with one paragraph and a swapped city name will get you penalized. We cover the right way in our service-area pages guide.",
      },
    ],
  },
  {
    category: "Web Design",
    slug: "web-design",
    metaTitle: "Colorado Web Design Guides | Learn (Blog)",
    metaDescription:
      "Web design articles for Colorado small businesses. Why your site isn't getting calls, mobile mistakes that cost bookings, and conversion fixes that work.",
    h1: "Colorado web design guides for service businesses",
    eyebrow: "Colorado Web Design Guide",
    intro:
      "What separates a website that gets calls from one that just sits there. Real-world web design lessons from 25+ years building sites for Colorado contractors, clinics, and shops.",
    body: [
      "70% of your traffic is on a phone. In Colorado especially — homeowners walk into the back yard at 8pm, see a leaking gutter, and Google 'gutter repair near me' from the lawn. If your site loads slowly, hides the phone number, or makes them pinch-zoom to read, they're gone in 4 seconds and onto your competitor.",
      "These articles cover the conversion-killers we find on almost every Colorado small-business website, plus the mobile-first fixes that actually move booking rates — not vanity metrics.",
      "Every guide is grounded in what we've shipped: hand-coded, fast, mobile-first websites for HVAC, plumbing, roofing, electrical, dental, legal, and other Colorado businesses across the Front Range.",
    ],
    highlights: [
      "Mobile-first — built for the way Colorado customers actually search",
      "Conversion focused (calls > clicks > impressions)",
      "Hand-coded, no bloated page builders",
    ],
    faqs: [
      {
        q: "What makes a Colorado small-business website actually convert?",
        a: "Click-to-call in the header, a clear local headline (city + service), a fast mobile load, real photos (not stock), and trust signals like reviews and license info above the fold. We break down all 10 conversion killers in our website-conversion guide.",
      },
      {
        q: "How fast should my website load on mobile in Colorado?",
        a: "Under 2.5 seconds Largest Contentful Paint on a mid-tier phone over LTE. Anything slower and your bounce rate climbs sharply — especially for emergency service searches.",
      },
    ],
  },
  {
    category: "Google Maps",
    slug: "google-maps",
    metaTitle: "Google Maps & Local Pack Guides for Colorado Businesses",
    metaDescription:
      "Rank in the Google Maps 3-pack across Colorado. Honest playbooks for Google Business Profile, reviews, and local-pack ranking — for service businesses.",
    h1: "Google Maps ranking guides for Colorado businesses",
    eyebrow: "Google Maps SEO Guide",
    intro:
      "How to dominate the Google Maps 3-pack in Colorado Springs, Denver, and the surrounding Front Range — without buying ads or paying a 'GBP optimization' upsell.",
    body: [
      "For most Colorado service businesses, the Google Maps 3-pack is more valuable than the entire organic search result below it. Customers click the map, scan three options, and call the one with the most relevant reviews. If you're not in those three slots in your city, you're invisible.",
      "These articles walk through the exact Google Business Profile setup, category selection, review strategy, and proximity tactics that put real Colorado HVAC, plumbing, roofing, and electrical companies into the top 3 — and keep them there.",
      "No suspension-risk gimmicks, no fake address tactics, no review-gating. Everything here is what we do for our own paying clients.",
    ],
    highlights: [
      "Specific to Colorado proximity and competition patterns",
      "Suspension-safe — no shortcuts that get accounts killed",
      "Same playbook used by paying CWI clients",
    ],
    faqs: [
      {
        q: "How fast can I get into the Google Maps 3-pack in Colorado Springs?",
        a: "If you have a real address inside the city, a properly categorized profile, and you start collecting reviews, most Colorado service businesses see 3-pack movement within 30–90 days. Saturated categories like roofing in metro Denver take longer.",
      },
      {
        q: "Does proximity to the searcher really matter that much?",
        a: "Yes. In most Colorado markets proximity is one of the top 3 ranking factors. That's why a Colorado Springs business will outrank a Denver competitor for searches happening in The Springs — even if Denver has more reviews.",
      },
    ],
  },
  {
    category: "AI & GEO",
    slug: "ai-geo",
    metaTitle: "AI Search & GEO Guides for Colorado Small Businesses",
    metaDescription:
      "What ChatGPT, Perplexity, and Google AI mean for your Colorado business. Generative Engine Optimization (GEO) explained without hype — plain English.",
    h1: "AI search & GEO guides for Colorado businesses",
    eyebrow: "AI Search & GEO Guide",
    intro:
      "Generative Engine Optimization (GEO) explained without the hype. What's actually changing in how Colorado customers find local services — and what to do about it in 2026.",
    body: [
      "Customers in Colorado are increasingly asking ChatGPT, Perplexity, and Google's AI Overviews questions like 'best HVAC company in Colorado Springs' or 'who installs metal roofs in Castle Rock.' If your business isn't being cited in those AI answers, you're losing leads to whoever is.",
      "These articles cover what GEO actually is (and isn't), how AI engines pick which businesses to mention, and the practical steps every Colorado small business should take in 2026 to stay visible as search shifts.",
      "Spoiler: most of it is just doing the SEO and content fundamentals well. The agencies charging $5k/month for 'AI optimization' are mostly selling you a rebrand of basic work.",
    ],
    highlights: [
      "No hype — what's real, what's noise",
      "Practical 2026 checklist for Colorado businesses",
      "Owner-written by a 25-year veteran",
    ],
    faqs: [
      {
        q: "Will AI search replace Google for local Colorado searches?",
        a: "No — Google Maps and traditional local pack results still drive the majority of phone calls for Colorado service businesses. AI search is additive: a growing slice of research-phase queries that you should also show up in.",
      },
      {
        q: "What's the single most important GEO move for a Colorado business?",
        a: "Publish substantive, locally-specific content on your own website (not just GBP posts). AI engines lean heavily on what your own site says about your services and service areas.",
      },
    ],
  },
  {
    category: "Pricing & Budget",
    slug: "pricing-and-budget",
    metaTitle: "Marketing Pricing & Budget Guides for Colorado Businesses",
    metaDescription:
      "Honest marketing pricing for Colorado small businesses. Real website cost ranges, fair SEO retainers, and how much to actually spend on marketing in 2026.",
    h1: "Marketing pricing & budget guides for Colorado businesses",
    eyebrow: "Marketing Pricing Guide",
    intro:
      "Real numbers, real ranges, no salesy 'it depends.' Honest pricing benchmarks for Colorado small-business websites, SEO, and marketing budgets in 2026.",
    body: [
      "Most Colorado business owners get quoted wildly different prices for the same work — anywhere from $500 to $50,000 for a website. The reason isn't quality. It's that the agency model rewards opacity, and 'it depends' is a sales tactic.",
      "These articles give you real ranges, what drives price up or down, and what a fair Colorado SEO retainer or website investment actually buys in 2026 — so you can negotiate from a position of knowledge instead of guesswork.",
      "Written by an owner who has quoted thousands of Colorado projects over 25+ years. No upsell, no inflated numbers.",
    ],
    highlights: [
      "Real Colorado-market price ranges",
      "Plain-English 'where the money goes' breakdowns",
      "Negotiate from knowledge, not guesswork",
    ],
    faqs: [
      {
        q: "What should a small Colorado business spend on marketing per month?",
        a: "A common benchmark is 5–10% of revenue for established businesses and 10–20% for growth-mode service businesses. We break down by industry in our marketing-budget guide.",
      },
      {
        q: "What's a fair monthly SEO retainer for a Colorado service business?",
        a: "For a single Colorado city, $1,500–$3,500/month buys real, in-house work. Anything under $750/month is almost always automated junk; anything above $5k/month for a single-city service business should come with very specific deliverables.",
      },
    ],
  },
  {
    category: "Buyer's Guide",
    slug: "buyers-guide",
    metaTitle: "Buyer's Guides: Hiring a Colorado SEO or Web Design Agency",
    metaDescription:
      "Before you hire a Colorado SEO or web design agency, read these. Red flags, must-ask questions, and how to spot outsourcing or whitelabel work.",
    h1: "Buyer's guides for hiring a Colorado marketing agency",
    eyebrow: "Marketing Agency Buyer's Guide",
    intro:
      "Before you sign a 12-month contract with anyone, read these. Real questions to ask, red flags to walk away from, and how to spot a Colorado agency that quietly outsources every project.",
    body: [
      "We get a steady stream of new Colorado clients who came to us after a bad first agency experience — a pretty website that didn't rank, a $4k/month SEO retainer with zero reporting, or a contract that auto-renewed for a year of nothing.",
      "These guides give you the exact questions to ask, the red flags that should end the conversation, and the situations where Colorado Web Impressions itself is not the right fit (yes, really).",
      "If we ever do anything described in our 'red flags' guide, you should fire us. That's the standard.",
    ],
    highlights: [
      "Built from 25+ years cleaning up other agencies' messes",
      "Includes when CWI is NOT the right fit",
      "Specific questions, not vague advice",
    ],
    faqs: [
      {
        q: "What's the #1 question to ask any Colorado marketing agency?",
        a: "'Who specifically will be doing the work, where do they sit, and can I talk to them before I sign?' If the answer is vague, the work is being outsourced.",
      },
      {
        q: "Should I sign a 12-month contract?",
        a: "Only if the deliverables are spelled out in writing month-by-month and there's a clear cancellation clause. Most quality Colorado agencies will offer month-to-month for SEO and PPC after an initial setup period.",
      },
    ],
  },
  {
    category: "Reviews & Reputation",
    slug: "reviews-and-reputation",
    metaTitle: "Google Reviews & Reputation Guides for Colorado Businesses",
    metaDescription:
      "How Colorado service businesses get more 5-star Google reviews — the right way. Templates, timing, and the review-gating mistake that gets accounts suspended.",
    h1: "Reviews & reputation guides for Colorado businesses",
    eyebrow: "Online Reviews & Reputation Guide",
    intro:
      "How to get more 5-star Google reviews from Colorado customers without breaking Google's rules — plus how to handle the inevitable bad review without panicking.",
    body: [
      "Reviews drive both Maps ranking and click-through. A Colorado plumber with 180 reviews at 4.8 stars will out-book a competitor with 25 reviews at 5.0 — every single time.",
      "These guides cover the exact ask, the timing, and the SMS / email templates Colorado service businesses use to consistently turn happy customers into 5-star reviews — without falling into review-gating traps that suspend Google profiles.",
      "Built around what works for 1–50 employee Colorado service businesses, not enterprise reputation tools.",
    ],
    highlights: [
      "Templates you can paste in today",
      "Suspension-safe — no review gating",
      "Specific to Colorado service businesses",
    ],
    faqs: [
      {
        q: "How many Google reviews does a Colorado business need to compete?",
        a: "In most Colorado service categories, 50+ reviews at 4.7+ stars is the minimum to compete in the 3-pack. 150+ in saturated categories like roofing or HVAC in metro Denver.",
      },
      {
        q: "Can I offer a discount for a Google review?",
        a: "No. That violates Google's policies and can get your profile suspended. You can ask for honest feedback after good service — that's it.",
      },
    ],
  },
  {
    category: "Marketing Strategy",
    slug: "marketing-strategy",
    metaTitle: "Marketing Strategy Guides for Colorado Small Businesses",
    metaDescription:
      "Where to spend your next marketing dollar — SEO vs PPC, when to add social, when to fix the website first. Honest strategy for Colorado service businesses.",
    h1: "Marketing strategy guides for Colorado small businesses",
    eyebrow: "Local Marketing Strategy Guide",
    intro:
      "Where to spend your next marketing dollar — SEO, Google Ads, social, or just fixing the website first. Honest strategy frameworks for Colorado service businesses.",
    body: [
      "Most Colorado small-business owners don't have a marketing problem — they have a sequencing problem. They're running Google Ads to a website that doesn't convert, or pumping money into social media when their Google Business Profile is barely set up.",
      "These guides walk through the right order of operations for Colorado service businesses: when SEO wins long-term, when PPC is the smart short-term play, and when neither is the answer until you fix something more fundamental.",
      "No 'do everything at once' nonsense. Real prioritization for owners with limited budget and limited time.",
    ],
    highlights: [
      "Right order of operations for Colorado service businesses",
      "Budget-by-budget recommendations",
      "Honest about what's NOT working for you yet",
    ],
    faqs: [
      {
        q: "Should a new Colorado business start with SEO or Google Ads?",
        a: "Almost always Google Ads first for fast lead flow, while SEO and Google Business Profile build in parallel. Once organic kicks in (typically month 4–8), you can scale ads down or up based on ROI.",
      },
      {
        q: "Is social media worth it for a Colorado home-service business?",
        a: "For most home-service businesses (HVAC, plumbing, roofing, electrical), social media is a low-priority channel. Spend that time and money on Google Maps, reviews, and your website first.",
      },
    ],
  },
];

export function getTopicLanding(slug: string): TopicLanding | undefined {
  return TOPIC_LANDINGS.find((t) => t.slug === slug);
}

export function getTopicLandingByCategory(
  category: ArticleCategory,
): TopicLanding | undefined {
  return TOPIC_LANDINGS.find((t) => t.category === category);
}

export function getArticlesByCategory(category: ArticleCategory): Article[] {
  return ARTICLES.filter((a) => a.category === category);
}

/** Common English words to ignore when scoring title/desc keyword overlap. */
const STOPWORDS = new Set([
  "a", "an", "and", "or", "but", "the", "of", "for", "to", "in", "on", "at",
  "by", "with", "from", "is", "are", "was", "were", "be", "been", "being",
  "this", "that", "these", "those", "it", "its", "your", "you", "we", "our",
  "us", "they", "them", "their", "i", "my", "me", "as", "if", "so", "do",
  "does", "did", "not", "no", "yes", "than", "then", "what", "when", "where",
  "why", "how", "who", "which", "any", "all", "more", "most", "some", "much",
  "very", "just", "about", "into", "out", "up", "down", "over", "under",
  "after", "before", "between", "without", "around", "again", "still", "even",
  "only", "also", "really", "actually", "right", "way", "ways", "thing",
  "things", "get", "gets", "got", "make", "makes", "made", "use", "used",
  "uses", "should", "would", "could", "can", "will", "may", "might", "must",
  "have", "has", "had", "having", "co", "colorado", "business", "businesses",
  "small", "owner", "owners", "company", "companies", "agency", "agencies",
]);

function tokenize(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .split(/\s+/)
      .filter((w) => w.length >= 4 && !STOPWORDS.has(w))
  );
}

/**
 * Find related articles for a given article slug.
 *
 * Scoring (multi-signal so results stay diverse and editorially sensible):
 *   • Curated NEXT_READ_MAP match → +10 (always surfaces first when configured)
 *   • Same category                → +3
 *   • Shared specific industry      → +1 each
 *   • Shared "All Industries" tag   → +0.5
 *   • Shared free-form tag          → +1.5 each
 *   • Title/desc keyword overlap    → +0.5 per shared meaningful word (capped at +3)
 *   • Recency tiebreaker            → +0.01 × days-since-current-published-anchor
 *
 * Excludes the current article. Returns up to `limit` (default 3).
 */
export function getRelatedArticles(currentSlug: string, limit = 3): Article[] {
  const current = ARTICLES.find((a) => a.slug === currentSlug);
  if (!current) {
    return ARTICLES.filter((a) => a.slug !== currentSlug).slice(0, limit);
  }

  const currentTokens = tokenize(`${current.title} ${current.desc}`);
  const currentTags = new Set(current.tags ?? []);
  const curatedNext = NEXT_READ_MAP[currentSlug];

  const scored = ARTICLES.filter((a) => a.slug !== currentSlug).map((a) => {
    let score = 0;

    // Editorial pin
    if (a.slug === curatedNext) score += 10;

    // Category match
    if (a.category === current.category) score += 3;

    // Industry overlap
    const sharedIndustries = a.industries.filter((i) =>
      current.industries.includes(i)
    );
    for (const ind of sharedIndustries) {
      score += ind === "All Industries" ? 0.5 : 1;
    }

    // Free-form tag overlap
    if (a.tags) {
      for (const t of a.tags) if (currentTags.has(t)) score += 1.5;
    }

    // Title/description keyword overlap (capped so it doesn't dominate)
    const aTokens = tokenize(`${a.title} ${a.desc}`);
    let kwScore = 0;
    for (const t of aTokens) if (currentTokens.has(t)) kwScore += 0.5;
    score += Math.min(kwScore, 3);

    // Recency tiebreaker — newer articles edge out older ones at equal score
    if (a.datePublished) {
      const days = (Date.parse(a.datePublished) || 0) / (1000 * 60 * 60 * 24);
      score += days * 0.0001;
    }

    return { article: a, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.article);
}

/**
 * Returns the editorially curated "next read" for an article, if one is set.
 * Used to render a distinct "Next up" CTA separate from algorithmic related
 * picks. Falls back to the top algorithmic match when no curation exists.
 */
export function getCuratedNextRead(currentSlug: string): Article | undefined {
  const explicit = NEXT_READ_MAP[currentSlug];
  if (explicit) {
    const a = ARTICLES.find((x) => x.slug === explicit);
    if (a) return a;
  }
  return getRelatedArticles(currentSlug, 1)[0];
}

/**
 * Look up an article by its full slug path (e.g. "/learn/local-seo-guide").
 * Used by route head() builders to derive per-category OG images.
 */
export function getArticleBySlug(slug: string): Article | undefined {
  return ARTICLES.find((a) => a.slug === slug);
}
